// src/components/flight-data/BreakdownArea.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  Chip,
  Paper
} from '@mui/material';
import ConcourseMap from './ConcourseMap';
import { calculateConnectionTime, isOvernightFlight } from '../../utils/connectionUtils';

function BreakdownArea({ tdrData, brockData, inboundETA, params }) {
  const inboundGate = tdrData?.inbound?.arrivalGate || '';
  
  const [gateGroups, setGateGroups] = useState({
    oddsEvens: { odds: [], evens: [] },
    eastWest: { east: [], west: [] },
    sectors: { sector1: [], sector2: [], sector3: [] },
    quadrants: { nw: [], sw: [], ne: [], se: [] }
  });

  const [hotGates, setHotGates] = useState([]);

  // --- MODIFIED: Wrapped constant objects in useMemo to prevent re-creation on every render ---
  const sectors = useMemo(() => ({
    sector1: Array.from({ length: 17 }, (_, i) => i + 23), // 23-39
    sector2: Array.from({ length: 16 }, (_, i) => i + 40), // 40-55
    sector3: Array.from({ length: 12 }, (_, i) => i + 56)  // 56-67
  }), []);
  
  const quadrants = useMemo(() => ({
    nw: [23, 25, 27, 29, 31, 33, 35, 37, 39, 41, 43],
    sw: [24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46],
    ne: [45, 47, 49, 51, 53, 55, 57, 59, 61, 63, 65, 67],
    se: [48, 50, 52, 54, 56, 58, 60, 62, 64, 66]
  }), []);

  const getConnectionMinutes = useCallback((flight) => {
    if (!flight?.etd || !inboundETA) return null;
    return calculateConnectionTime(flight.etd, inboundETA, isOvernightFlight(flight));
  }, [inboundETA]);

  const countStopsForGates = (gates) => {
    if (!gates || gates.length === 0) return 0;
    return gates.length;
  };

  const countBagsForGates = (gates) => {
    if (!tdrData || !tdrData.outbound || gates.length === 0) return 0;
    return tdrData.outbound.reduce((total, flight) => {
      const flightGate = parseInt(flight.gate?.replace(/\D/g, ''), 10);
      if (gates.includes(flightGate)) {
        return total + (flight.bags || 0);
      }
      return total;
    }, 0);
  };

  useEffect(() => {
    if (!tdrData || !tdrData.outbound) return;
    
    const identifiedHotGates = new Set();
    tdrData.outbound.forEach(flight => {
        const connectionTime = getConnectionMinutes(flight);
        if (connectionTime !== null && connectionTime <= 30) {
            const gateNum = parseInt(flight.gate?.replace(/\D/g, ''), 10);
            if (!isNaN(gateNum)) {
                identifiedHotGates.add(gateNum);
            }
        }
    });
    setHotGates(Array.from(identifiedHotGates));

    const outboundGates = Array.from(new Set(
      tdrData.outbound
        .map(flight => parseInt(flight.gate?.replace(/\D/g, ''), 10))
        .filter(gate => !isNaN(gate))
    ));
    
    const odds = outboundGates.filter(gate => gate % 2 !== 0).sort((a,b) => a - b);
    const evens = outboundGates.filter(gate => gate % 2 === 0).sort((a,b) => a - b);
    
    const west = outboundGates.filter(gate => gate >= 23 && gate <= 47).sort((a,b) => a - b);
    const east = outboundGates.filter(gate => gate >= 48 && gate <= 67).sort((a,b) => a - b);
    
    const sector1Gates = outboundGates.filter(gate => sectors.sector1.includes(gate)).sort((a,b) => a - b);
    const sector2Gates = outboundGates.filter(gate => sectors.sector2.includes(gate)).sort((a,b) => a - b);
    const sector3Gates = outboundGates.filter(gate => sectors.sector3.includes(gate)).sort((a,b) => a - b);
    
    const nwGates = outboundGates.filter(gate => quadrants.nw.includes(gate)).sort((a,b) => a - b);
    const swGates = outboundGates.filter(gate => quadrants.sw.includes(gate)).sort((a,b) => a - b);
    const neGates = outboundGates.filter(gate => quadrants.ne.includes(gate)).sort((a,b) => a - b);
    const seGates = outboundGates.filter(gate => quadrants.se.includes(gate)).sort((a,b) => a - b);
    
    setGateGroups({
      oddsEvens: { odds, evens }, eastWest: { east, west },
      sectors: { sector1: sector1Gates, sector2: sector2Gates, sector3: sector3Gates },
      quadrants: { nw: nwGates, sw: swGates, ne: neGates, se: seGates }
    });
  // --- MODIFIED: The dependency array is now correct and stable ---
  }, [ tdrData, inboundGate, inboundETA, quadrants, sectors, getConnectionMinutes ]);

  const getOutboundGateNumbers = () => {
    if (!tdrData || !tdrData.outbound) return [];
    const coldMinutes = (params?.coldThresholdHours ?? 2) * 60;
    return Array.from(new Set(
      tdrData.outbound
        .filter(flight => {
          // Cold connections ride to the cold sort, not the gate — a gate
          // only lights as active if at least one non-cold flight stops
          // there. Unparseable times stay lit (benefit of the doubt).
          const minutes = getConnectionMinutes(flight);
          return minutes === null || minutes < coldMinutes;
        })
        .map(flight => parseInt(flight.gate?.replace(/\D/g, ''), 10))
        .filter(gate => !isNaN(gate))
    ));
  };
  
  // This helper function now correctly renders the colored gate numbers
  const formatAndColorGates = (gates) => {
    if (!gates || gates.length === 0) return 'None';
    return gates.map((gate, index) => (
        <span key={gate} style={hotGates.includes(gate) ? { color: '#FF0000', fontWeight: 'bold' } : {}}>
          C{gate}{index < gates.length - 1 ? ', ' : ''}
        </span>
      ));
  }

  return (
    <Box sx={{ mb: 0 }}>
      <ConcourseMap 
        tdrData={tdrData}
        activeGates={getOutboundGateNumbers()}
        inboundGate={inboundGate}
        hotGates={hotGates}
      />

      <Grid container spacing={2}>
        {/* Odds / Evens */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{
            width: '100%',
            backgroundColor: 'rgba(30, 30, 30, 0.7)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: '100%' 
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#F9B612', mb: 1 }}>Odds / Evens</Typography>
              <Divider sx={{ mb: 2, backgroundColor: 'rgba(249, 182, 18, 0.3)' }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>Odds (North Side)</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2' }}>
                      Gates: {formatAndColorGates(gateGroups.oddsEvens.odds)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--amber"
                      label={`${countBagsForGates(gateGroups.oddsEvens.odds)} Bags, ${countStopsForGates(gateGroups.oddsEvens.odds)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(249, 182, 18, 0.7)', color: '#000' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>Evens (South Side)</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2' }}>
                      Gates: {formatAndColorGates(gateGroups.oddsEvens.evens)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--amber"
                      label={`${countBagsForGates(gateGroups.oddsEvens.evens)} Bags, ${countStopsForGates(gateGroups.oddsEvens.evens)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(249, 182, 18, 0.7)', color: '#000' }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* East / West */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{
            width: '100%',
            backgroundColor: 'rgba(30, 30, 30, 0.7)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: '100%' 
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#FF0000', mb: 1 }}>East / West</Typography>
              <Divider sx={{ mb: 2, backgroundColor: 'rgba(255, 0, 0, 0.3)' }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>East (C48-C67)</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2' }}>
                      Gates: {formatAndColorGates(gateGroups.eastWest.east)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--red"
                      label={`${countBagsForGates(gateGroups.eastWest.east)} Bags, ${countStopsForGates(gateGroups.eastWest.east)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(255, 0, 0, 0.7)', color: '#fff' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>West (C23-C47)</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2' }}>
                      Gates: {formatAndColorGates(gateGroups.eastWest.west)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--red"
                      label={`${countBagsForGates(gateGroups.eastWest.west)} Bags, ${countStopsForGates(gateGroups.eastWest.west)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(255, 0, 0, 0.7)', color: '#fff' }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sectors */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{
            width: '100%',
            backgroundColor: 'rgba(30, 30, 30, 0.7)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: '100%' 
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#4CAF50', mb: 1 }}>Sectors</Typography>
              <Divider sx={{ mb: 2, backgroundColor: 'rgba(76, 175, 80, 0.3)' }} />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>Sector 1 (23-39)</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2' }}>
                      Gates: {formatAndColorGates(gateGroups.sectors.sector1)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--green"
                      label={`${countBagsForGates(gateGroups.sectors.sector1)} Bags, ${countStopsForGates(gateGroups.sectors.sector1)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(76, 175, 80, 0.7)', color: '#000' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>Sector 2 (40-55)</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2' }}>
                      Gates: {formatAndColorGates(gateGroups.sectors.sector2)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--green"
                      label={`${countBagsForGates(gateGroups.sectors.sector2)} Bags, ${countStopsForGates(gateGroups.sectors.sector2)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(76, 175, 80, 0.7)', color: '#000' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>Sector 3 (56-67)</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2' }}>
                      Gates: {formatAndColorGates(gateGroups.sectors.sector3)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--green"
                      label={`${countBagsForGates(gateGroups.sectors.sector3)} Bags, ${countStopsForGates(gateGroups.sectors.sector3)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(76, 175, 80, 0.7)', color: '#000' }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Quadrants */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{
            width: '100%',
            backgroundColor: 'rgba(30, 30, 30, 0.7)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            height: '100%' 
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#2196F3', mb: 1 }}>Quadrants</Typography>
              <Divider sx={{ mb: 2, backgroundColor: 'rgba(33, 150, 243, 0.3)' }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>North West</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2', fontSize: '0.85rem' }}>
                      Gates: {formatAndColorGates(gateGroups.quadrants.nw)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--blue"
                      label={`${countBagsForGates(gateGroups.quadrants.nw)} Bags, ${countStopsForGates(gateGroups.quadrants.nw)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(33, 150, 243, 0.7)', color: '#000' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>North East</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2', fontSize: '0.85rem' }}>
                      Gates: {formatAndColorGates(gateGroups.quadrants.ne)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--blue"
                      label={`${countBagsForGates(gateGroups.quadrants.ne)} Bags, ${countStopsForGates(gateGroups.quadrants.ne)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(33, 150, 243, 0.7)', color: '#000' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>South West</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2', fontSize: '0.85rem' }}>
                      Gates: {formatAndColorGates(gateGroups.quadrants.sw)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--blue"
                      label={`${countBagsForGates(gateGroups.quadrants.sw)} Bags, ${countStopsForGates(gateGroups.quadrants.sw)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(33, 150, 243, 0.7)', color: '#000' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper className="breakdown-subcard" sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="subtitle1" sx={{ color: '#e2e2e2', fontWeight: 'bold' }}>South East</Typography>
                    <Typography component="div" sx={{ color: '#e2e2e2', fontSize: '0.85rem' }}>
                      Gates: {formatAndColorGates(gateGroups.quadrants.se)}
                    </Typography>
                    <Chip
                      className="flight-data-chip flight-data-chip--blue"
                      label={`${countBagsForGates(gateGroups.quadrants.se)} Bags, ${countStopsForGates(gateGroups.quadrants.se)} stops`}
                      size="small"
                      sx={{ mt: 1, backgroundColor: 'rgba(33, 150, 243, 0.7)', color: '#000' }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default BreakdownArea;
